export const STILL_WRITING_SCENE = 'Still writing this scene…';
export const NEW_PASS_ON_THE_WAY = 'A new pass is on the way. This draft stays until it arrives.';
export const DRAFT_DID_NOT_ARRIVE = "That draft didn't come through. Please try again.";
export const SCENE_WRITE_POLL_MS = 2000;
export const SCENE_WRITE_STALE_MS = 90000;

/**
 * in_progress with a fresh heartbeat means the server is still writing.
 * A stale heartbeat, or a failed mark, means that draft did not arrive.
 * done and idle leave the line off; a finished pass is the draft itself.
 */
export function sceneGenerationView(generation, now = Date.now()) {
  const state = generation?.state || 'idle';
  if (state === 'failed') {
    return { kind: 'stopped', message: DRAFT_DID_NOT_ARRIVE };
  }
  if (state !== 'in_progress') {
    return { kind: 'idle', message: '' };
  }
  const at = Date.parse(generation.last_progress_at || '');
  if (!Number.isFinite(at) || now - at > SCENE_WRITE_STALE_MS) {
    return { kind: 'stopped', message: DRAFT_DID_NOT_ARRIVE };
  }
  return { kind: 'writing', message: STILL_WRITING_SCENE };
}

const followKey = (storyId) => `brainstormy.scene-writing.follow.${storyId || ''}`;
const lastSceneKey = (storyId) => `brainstormy.scene-writing.last-scene.${storyId || ''}`;

/** The scene this book was open on. A reload without ?scene= uses it. */
export function rememberLastScene(storyId, sceneId) {
  if (!storyId || !sceneId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(lastSceneKey(storyId), String(sceneId));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readLastScene(storyId) {
  if (!storyId || typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(lastSceneKey(storyId)) || '';
  } catch {
    return '';
  }
}

/** Remember this Write across a refresh. The reloaded page has no in-memory busy flag. */
export function rememberSceneWrite(storyId, { sceneId = '', priorPassId = '' } = {}) {
  if (!storyId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(followKey(storyId), JSON.stringify({
      sceneId: String(sceneId || ''),
      priorPassId: String(priorPassId || ''),
      startedAt: Date.now(),
    }));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readSceneWrite(storyId) {
  if (!storyId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(followKey(storyId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !Number.isFinite(Number(data.startedAt))) return null;
    return {
      sceneId: String(data.sceneId || ''),
      priorPassId: String(data.priorPassId || ''),
      startedAt: Number(data.startedAt),
    };
  } catch {
    return null;
  }
}

export function clearSceneWrite(storyId) {
  if (!storyId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(followKey(storyId));
  } catch {
    /* ignore */
  }
}

/**
 * What the reloaded page should say.
 * A fresh server heartbeat wins. A Write remembered from this visit still
 * counts when compose comes back `done` or `idle` from the previous pass,
 * which is what a refresh during preparation sees. A different pass id
 * means the new draft has landed.
 */
export function followSceneWrite({
  generation,
  flag,
  passId = '',
  now = Date.now(),
} = {}) {
  const server = sceneGenerationView(generation, now);
  const current = String(passId || '');
  if (flag && current && current !== String(flag.priorPassId || '')) {
    return { kind: 'idle', message: '' };
  }
  if (server.kind === 'writing') return server;
  if (!flag) return server;
  const started = Number(flag.startedAt);
  const age = now - started;
  if (!Number.isFinite(started) || age > SCENE_WRITE_STALE_MS || age < -5000) {
    return server.kind === 'idle'
      ? { kind: 'stopped', message: DRAFT_DID_NOT_ARRIVE }
      : server;
  }
  if (server.kind === 'stopped') {
    const at = Date.parse(generation?.last_progress_at || '');
    if (Number.isFinite(at) && at + 5000 < started) {
      return { kind: 'writing', message: STILL_WRITING_SCENE };
    }
    return server;
  }
  return { kind: 'writing', message: STILL_WRITING_SCENE };
}

/**
 * Show a failed draft only when this visit's write failed.
 * A saved failed mark, a stale heartbeat, or an old follow flag
 * stays quiet on open. A live write still says it is writing.
 */
export function presentSceneWrite(view, { sessionFailure = false } = {}) {
  const base = view || { kind: 'idle', message: '' };
  if (base.kind === 'writing') return base;
  if (sessionFailure) return { kind: 'stopped', message: DRAFT_DID_NOT_ARRIVE };
  if (base.kind === 'stopped') return { kind: 'idle', message: '' };
  return base;
}
